import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { QualificationDto } from "@/core/features/qualifications/qualification.dto";
import { CreateQualificationDto } from "@/core/features/qualifications/create-qualification.dto";
import { UpdateQualificationDto } from "@/core/features/qualifications/update-qualification.dto";
import { OptionType } from "@/types/OptionType";
import { IRequest } from "@/interfaces/IRequest";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";

export class QualificationsResource extends ResourceBaseService<
  QualificationDto,
  CreateQualificationDto,
  UpdateQualificationDto
> {
  protected EntityDto = QualificationDto;
  protected CreateEntityDto = CreateQualificationDto;
  protected UpdateEntityDto = UpdateQualificationDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/qualifications");
  }

  archive(id: string): Promise<QualificationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/archive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  unarchive(id: string): Promise<QualificationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: this.resourceUrl + "/" + id + "/unarchive",
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  async optionLoader(inputValue: string, includeArchived = false): Promise<OptionType[]> {
    const request: IRequest = inputValue ? { search: inputValue, searchBy: ["id", "name"] } : {};
    request.sortBy = "name";
    applyOptionLoaderPagination(request);
    const { data } = await this.findAll(request, includeArchived);
    return data.map((value) => value.mapEntityToOptionType([value.name]));
  }

  async getByProject(projectId: string, request: IRequest) {
    return this.apiHttpClient
      .request<null, FindManyResult<QualificationDto>>(
        HttpRequest.create({
          url: this.resourceUrl + "/project/" + projectId,
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((findManyResult) => {
        findManyResult.data = plainToInstance(QualificationDto, findManyResult.data);
        return findManyResult;
      });
  }

  async importQualifications(file: File): Promise<{ status: "queued"; jobId: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.apiHttpClient.request<FormData, { status: "queued"; jobId: string }>(
      HttpRequest.create<FormData>({
        url: this.resourceUrl + "/import",
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );
  }

  async downloadImportTemplate(): Promise<void> {
    return this.downloadExcel(null, "/import/template", "plantilla-formaciones");
  }
}
